import React, { useEffect, useMemo, useState } from "react";
import { api, fmtUSD } from "../lib/api";
import SeverityBadge from "../components/SeverityBadge";
import ServicePill from "../components/ServicePill";
import LayerBadge from "../components/LayerBadge";
import LifecyclePill from "../components/LifecyclePill";
import OpportunityDetailModal from "../components/OpportunityDetailModal";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Button } from "../components/ui/button";
import { Search, Filter, CheckCircle2, Eye, X } from "lucide-react";

const SERVICES = [
  "all",
  "BigQuery",
  "Vertex AI",
  "Compute Engine",
  "Cloud Storage",
  "GKE",
  "Cloud SQL",
  "Cloud Run",
  "IAM",
  "Cloud Operations Suite",
  "Cloud Pub/Sub",
  "EC2",
  "EBS",
  "S3",
  "RDS",
  "Lambda",
  "DynamoDB",
  "ELB",
  "Virtual Machines",
  "Azure SQL",
  "Blob Storage",
  "AKS",
  "Managed Disks",
  "Azure OpenAI",
  "Azure Monitor",
  "Reservations",
  "OCI Compute",
  "Autonomous Database",
  "Object Storage",
  "OKE",
  "Block Volume",
  "Load Balancer",
  "Data Science",
  "Universal Credits",
];
const PROVIDERS = ["all", "gcp", "aws", "azure", "oci"];
const SEVERITIES = ["all", "critical", "high", "medium", "low"];

export default function Opportunities() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [service, setService] = useState("all");
  const [severity, setSeverity] = useState("all");
  const [provider, setProvider] = useState("all");
  const [projectId, setProjectId] = useState("all");
  const [minSavings, setMinSavings] = useState("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.opportunities({
        service: service !== "all" ? service : null,
        severity: severity !== "all" ? severity : null,
        provider: provider !== "all" ? provider : null,
        project_id: projectId !== "all" ? projectId : null,
        min_savings: minSavings ? Number(minSavings) : null,
      });
      setItems(data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service, severity, provider, projectId, minSavings]);

  const projectsAvailable = useMemo(() => {
    const set = new Set(items.map((o) => o.project_id));
    return ["all", ...Array.from(set)];
  }, [items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (o) =>
        o.resource_name?.toLowerCase().includes(q) ||
        o.opportunity_type?.toLowerCase().includes(q) ||
        o.project_id?.toLowerCase().includes(q),
    );
  }, [items, search]);

  const totalSavings = filtered.reduce(
    (s, o) => s + (o.estimated_monthly_savings_usd || 0),
    0,
  );

  const resetFilters = () => {
    setService("all");
    setSeverity("all");
    setProjectId("all");
    setMinSavings("");
    setSearch("");
  };

  return (
    <div className="px-6 lg:px-10 py-8 space-y-6" data-testid="opportunities-page">
      {/* Filters */}
      <div className="cs-card p-4 flex flex-wrap items-center gap-3" data-testid="filters-bar">
        <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-[0.14em]">
          <Filter className="w-3.5 h-3.5" />
          Filters
        </div>

        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="opportunities-search"
            placeholder="Resource, project, or type…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 rounded-sm h-9"
          />
        </div>

        <Select value={provider} onValueChange={setProvider}>
          <SelectTrigger className="w-[120px] rounded-sm h-9" data-testid="filter-provider">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PROVIDERS.map((p) => (
              <SelectItem key={p} value={p} data-testid={`filter-provider-${p}`}>
                {p === "all" ? "All clouds" : p.toUpperCase()}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={service} onValueChange={setService}>
          <SelectTrigger className="w-[160px] rounded-sm h-9" data-testid="filter-service">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SERVICES.map((s) => (
              <SelectItem key={s} value={s} data-testid={`filter-service-${s}`}>
                {s === "all" ? "All services" : s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={severity} onValueChange={setSeverity}>
          <SelectTrigger className="w-[140px] rounded-sm h-9" data-testid="filter-severity">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SEVERITIES.map((s) => (
              <SelectItem key={s} value={s} data-testid={`filter-severity-${s}`}>
                {s === "all" ? "All severities" : s.charAt(0).toUpperCase() + s.slice(1)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={projectId} onValueChange={setProjectId}>
          <SelectTrigger className="w-[200px] rounded-sm h-9" data-testid="filter-project">
            <SelectValue placeholder="Project" />
          </SelectTrigger>
          <SelectContent>
            {projectsAvailable.map((p) => (
              <SelectItem key={p} value={p}>
                {p === "all" ? "All projects" : p}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Input
          data-testid="filter-min-savings"
          type="number"
          min="0"
          step="50"
          placeholder="min $/mo"
          value={minSavings}
          onChange={(e) => setMinSavings(e.target.value)}
          className="w-28 rounded-sm h-9"
        />

        <Button
          variant="outline"
          onClick={resetFilters}
          data-testid="reset-filters-button"
          className="rounded-sm h-9"
        >
          <X className="w-4 h-4 mr-1" /> Reset
        </Button>

        <div className="ml-auto text-xs text-muted-foreground">
          <span data-testid="opportunities-count" className="text-foreground font-medium">
            {filtered.length}
          </span>{" "}
          findings ·{" "}
          <span className="text-[#00579e] font-medium cs-mono">
            {fmtUSD(totalSavings)}
          </span>{" "}
          /mo
        </div>
      </div>

      {/* Table */}
      <div className="cs-card overflow-hidden" data-testid="opportunities-table-card">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[hsl(var(--cs-tint))] border-b border-border">
              <tr className="text-left">
                <th className="px-4 py-3 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground">Service</th>
                <th className="px-4 py-3 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground" title="Severity = how urgent the finding is (impact × confidence). Independent of remediation risk.">Severity</th>
                <th className="px-4 py-3 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground" title="WasteGraph 5-layer taxonomy: L1 Surface → L5 Architectural">Layer</th>
                <th className="px-4 py-3 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground">Opportunity</th>
                <th className="px-4 py-3 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground">Resource</th>
                <th className="px-4 py-3 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground">Project</th>
                <th className="px-4 py-3 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground">Status</th>
                <th className="px-4 py-3 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground text-right">Savings / mo</th>
                <th className="px-4 py-3 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground text-center" title="Auto-fixable = CloudScore can execute this safely with one click. ⚠ Requires write permissions on the target resource.">Auto</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={10} className="px-4 py-10 text-center text-muted-foreground">
                    Loading…
                  </td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={10} className="px-4 py-10 text-center text-muted-foreground" data-testid="no-opportunities">
                    No opportunities match the current filters.
                  </td>
                </tr>
              )}
              {!loading && filtered.map((o) => (
                <tr
                  key={o.id}
                  className="cs-row-hover border-b border-border last:border-0"
                  data-testid={`opp-row-${o.id}`}
                >
                  <td className="px-4 py-3"><ServicePill service={o.service} /></td>
                  <td className="px-4 py-3"><SeverityBadge severity={o.severity} /></td>
                  <td className="px-4 py-3"><LayerBadge layer={o.layer} /></td>
                  <td className="px-4 py-3 font-medium text-foreground">{o.opportunity_type}</td>
                  <td className="px-4 py-3 cs-mono text-[12px] text-muted-foreground max-w-[220px] truncate" title={o.resource_name}>
                    {o.resource_name}
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">{o.project_id}</td>
                  <td className="px-4 py-3"><LifecyclePill status={o.lifecycle_status} /></td>
                  <td className="px-4 py-3 text-right cs-mono font-semibold text-[#00579e]">
                    {fmtUSD(o.estimated_monthly_savings_usd)}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {o.auto_remediable ? (
                      <CheckCircle2
                        className="w-4 h-4 text-[#22c55e] inline"
                        title="Auto-remediable. ⚠ Requires write permissions on the target resource."
                      />
                    ) : (
                      <span
                        className="text-muted-foreground text-xs"
                        title="Manual remediation only — requires engineering review."
                      >
                        —
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      data-testid={`view-opp-${o.id}`}
                      onClick={() => setSelected(o)}
                      className="rounded-sm h-7 text-xs"
                    >
                      <Eye className="w-3.5 h-3.5 mr-1" /> View
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <OpportunityDetailModal
        open={!!selected}
        onClose={() => setSelected(null)}
        opportunity={selected}
        onChanged={load}
      />
    </div>
  );
}
